import { useState, useEffect, useMemo } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import axios from "axios";
import RaiseTickets from "../models/RaiseTickets";
import ActivatePopup from "../models/ActivatePopup";
import UnFilledPopup from "../models/UnFilledPopup";
import UnVerifiedPopup from "../models/UnverifiedPopup";
import ProcessingPopup from "../models/ProcessingPopup";
import FilterBar from "./FilterBar";
import LivePayoutDetailPopup from '../models/LivePayoutDetailPopup';
import PaymentSettlePopup from '../models/PaymentSettlePopup';
import BASE_URL from "../utils/Urls"
import activeImg from "../Assets/default-pic.png"

const MainTable = ({ searchText, filterOption, userIdFilters = {}, onTodayTotals, onDataRefresh, refreshKey = 0 }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActivatePopupOpen, setIsActivatePopupOpen] = useState(false);
  const [isUnFilledPopupOpen, setIsUnFilledPopupOpen] = useState(false);
  const [isUnVerifiedPopupOpen, setIsUnVerifiedPopupOpen] = useState(false);
  const [isProcessingPopupOpen, setIsProcessingPopupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ticketCounts, setTicketCounts] = useState({});
  const [openGroup, setOpenGroup] = useState(null);
  const [editingNameUserId, setEditingNameUserId] = useState(null);
  const [editingPhoneUserId, setEditingPhoneUserId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const [, setLockLoaded] = useState(false);

  useEffect(() => {
    const fetchLock = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/locks`);
        if (res.data?.lock) {
          console.log("🔐 Primary lock found:", res.data.lock);
          setLockLoaded(true);
        }
      } catch (err) {
        console.error("❌ Failed to fetch primary lock:", err);
      }
    };
    fetchLock();
  }, []);

  const [livePayoutDetailRow, setLivePayoutDetailRow] = useState(null);
  const [settleRow, setSettleRow] = useState(null);
  const [companyLocked, setCompanyLocked] = useState({ total: 0, by_month: [] });

  const openLivePayoutDetail = (row) => setLivePayoutDetailRow(row);
  const openSettle = (row) => setSettleRow(row);

  const handleEndLivePayout = async (row) => {
    const liveAmount = Number(row.livePayoutBills) || 0;
    const totalCredits = Number(row.totalCredits) || 0;
    if (liveAmount <= 0 && totalCredits <= 0) {
      alert("This user has no live payout amount or credits to end.");
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to end Live Payout for ${row.employer_name || row.user_id}?\n\n• Amount: $${liveAmount.toFixed(2)} (will move to Payment Due)\n• Credits: ${totalCredits} (will reset to 0)`
    );
    if (!confirmed) return;

    try {
      const res = await axios.post(`${BASE_URL}/admin/end-live-payout/${row.user_id}`, {
        livePayout: liveAmount,
        updated_by: localStorage.getItem('admin_user') || 'admin',
      });
      alert(res.data?.message || 'Live payout ended and credits reset to 0.');
      reloadTableData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to end live payout');
    }
  };

  const handleEditNameClick = (row) => {
    setEditingNameUserId(row.user_id);
    setEditName(row.employer_name);
  };
  const handleEditNameChange = (e) => setEditName(e.target.value);
  const handleEditNameSave = async (user_id) => {
    if (!editName.trim()) {
      alert('Please enter a name.');
      return;
    }
    setEditLoading(true);
    try {
      const res = await axios.put(`${BASE_URL}/users/admin/update/${user_id}`, { employer_name: editName });
      alert(res.data.message || 'Name updated');
      setEditingNameUserId(null);
      setEditName("");
      reloadTableData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update name');
    } finally {
      setEditLoading(false);
    }
  };
  const handleEditNameCancel = () => {
    setEditingNameUserId(null);
    setEditName("");
  };

  const handleEditPhoneClick = (row) => {
    setEditingPhoneUserId(row.user_id);
    setEditPhone(row.phone_number);
  };
  const handleEditPhoneChange = (e) => setEditPhone(e.target.value);
  const handleEditPhoneSave = async (user_id) => {
    if (!editPhone.trim()) {
      alert('Please enter a phone number.');
      return;
    }
    setEditLoading(true);
    try {
      const res = await axios.put(`${BASE_URL}/users/admin/update/${user_id}`, { phone_number: editPhone });
      alert(res.data.message || 'Phone updated');
      setEditingPhoneUserId(null);
      setEditPhone("");
      reloadTableData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update phone');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditPhoneCancel = () => {
    setEditingPhoneUserId(null);
    setEditPhone("");
  };

  const [editingPaymentDueUserId, setEditingPaymentDueUserId] = useState(null);
  const [editPaymentDue, setEditPaymentDue] = useState("");

  const handleEditPaymentDueClick = (row) => {
    setEditingPaymentDueUserId(row.user_id);
    setEditPaymentDue(String(row.paymentDue ?? 0));
  };
  const handleEditPaymentDueChange = (e) => setEditPaymentDue(e.target.value);
  const handleEditPaymentDueSave = async (user_id) => {
    if (editPaymentDue === "" || isNaN(Number(editPaymentDue))) {
      alert("Please enter a valid amount.");
      return;
    }
    setEditLoading(true);
    try {
      const res = await axios.put(`${BASE_URL}/users/admin/update-payment-due/${user_id}`, {
        paymentDue: Number(editPaymentDue),
        updated_by: localStorage.getItem('admin_user') || 'admin',
      });
      alert(res.data?.message || 'Payment Due updated');
      setEditingPaymentDueUserId(null);
      setEditPaymentDue("");
      reloadTableData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update payment due');
    } finally {
      setEditLoading(false);
    }
  };
  const handleEditPaymentDueCancel = () => {
    setEditingPaymentDueUserId(null);
    setEditPaymentDue("");
  };
  const [tableData, setTableData] = useState([]);
  const [allGroupsList, setAllGroupsList] = useState([]);

  const [selectedOption, setSelectedOption] = useState(filterOption || "All");
  const [userIdFiltersState, setUserIdFilters] = useState(userIdFilters || {});

  useEffect(() => {
    if (filterOption) setSelectedOption(filterOption);
  }, [filterOption]);
  const fetchTicketCount = async (user_id) => {
    try {
      const res = await axios.get(`${BASE_URL}/tickets/${user_id}`);
      setTicketCounts((prev) => ({
        ...prev,
        [user_id]: res.data.data.length || 0,
      }));
    } catch (err) {
      console.error(`Error fetching tickets for ${user_id}:`, err);
      setTicketCounts((prev) => ({
        ...prev,
        [user_id]: 0,
      }));
    }
  };

  const reloadTableData = () => {
    Promise.all([
      axios.get(`${BASE_URL}/users/`),
      axios.get(`${BASE_URL}/admin/dp-monthly-metrics`).catch(() => ({ data: { metrics: {} } })),
      axios.get(`${BASE_URL}/admin/company-credits`).catch(() => ({ data: { total: 0, by_month: [] } })),
      axios.get(`${BASE_URL}/groups/`).catch(() => ({ data: { result: [] } })),
    ])
      .then(([res, metricsRes, companyRes, groupsRes]) => {
        if (groupsRes?.data && Array.isArray(groupsRes.data.result)) {
          setAllGroupsList(groupsRes.data.result.map((g) => g.group).filter(Boolean));
        }
        const metrics = (metricsRes && metricsRes.data && metricsRes.data.metrics) || {};
        setCompanyLocked({
          total: Number(companyRes && companyRes.data && companyRes.data.total) || 0,
          by_month: (companyRes && companyRes.data && companyRes.data.by_month) || [],
        });
        if (res.data && res.data.users) {
          const formattedUsers = res.data.users.map((user) => {
            const m = metrics[user.user_id] || {};
            return {
            profile_img: user.profile_img || activeImg,
            login_image: user.login_image,
            user_id: user.user_id,
            email: user.email || "",
            phone_number: user.phone_number,
            status: formatStatus(user.status, user),
            status_code: user.status_code,
            employer_name: user.employer_name,
            joinedDate: user.created_at,
            updated_at: user.updated_at,
            onboarding_fee: user.onboarding_fee,
            livePayoutBills: Number(m.live_payout_bills ?? m.super_bonus ?? user.livePayoutBills ?? 0),
            liveAmountIsEditedBy: user.liveAmountIsEditedBy || "-",
            paymentDueIsEditedBy: user.paymentDueIsEditedBy || "-",
            paymentDue: user.paymentDue || 0,
            paidEarnings: user.paidEarnings || 0,
            bankedEarnings: user.bankedEarnings || 0,
            superBonus: Number(m.super_bonus) || 0,
            accountsCreated: Number(m.accounts_created) || 0,
            unrootedAccounts: Number(m.unrooted_accounts) || 0,
            totalCredits: Number(m.total_credits) || 0,
            companyCredits: Number(m.company_credits) || 0,
            tickets: 0,
            group: user.group || "Group",
            purpose: user.purpose || "Part-Time",
            aadhar_no: user.aadhar_no || "",
            aadhar_front_img: user.aadhar_front_img || "",
            aadhar_back_img: user.aadhar_back_img || "",
            pan_card_number: user.pan_card_number || "",
            pan_front_img: user.pan_front_img || "",
            ifsc_code: user.ifsc_code || "",
            bank_account_no: user.bank_account_no || "",
            nominee_name: user.nominee_name || "",
            nominee_phone_no: user.nominee_phone_no || "",
            main_employee_code: user.main_employee_code || "",
            transaction_id: user.transaction_id || "",
            transaction_img: user.transaction_img || "",
            approved: (() => {
              if (Array.isArray(user.approved)) return user.approved;
              try {
                const parsed = JSON.parse(user.approved);
                return Array.isArray(parsed) ? parsed : [];
              } catch (_) {
                return [];
              }
            })(),
            rejected: (() => {
              if (Array.isArray(user.rejected)) return user.rejected;
              try {
                const parsed = JSON.parse(user.rejected);
                return Array.isArray(parsed) ? parsed : [];
              } catch (_) {
                return [];
              }
            })(),
            coupon_code: user.coupon_code,
            secondary_coupon_code: user.secondary_coupon_code,
            valid_date: formatDate(user.valid_date),
            expired_date: formatDate(user.expired_date),
            redeemed: user.redeem,
            secondary_redeemed: user.secondary_redeemed,
            deactivate_reason: user.deactivate_reason,
            is_online: user.is_online === 1 || user.is_online === true ? 1 : 0,
            resubmitted: (() => {
              if (!user.updated_at || !user.created_at) return false;
              if (user.status === 'active') return false;
              if (user.status_code === 0) return false;
              return new Date(user.updated_at).getTime() > new Date(user.created_at).getTime() + 60000;
            })()
            };
          });
          setTableData(formattedUsers);
          res.data.users.forEach((u) => fetchTicketCount(u.user_id));
          console.log("Formatted Users:", formattedUsers);
        }
        if (typeof onDataRefresh === "function") onDataRefresh();
      })
      .catch((err) => console.error("API Error:", err));
  };

  useEffect(() => {
    reloadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const toggleOnlineStatus = async (row) => {
    try {
      const next = row.is_online ? 0 : 1;
      const res = await axios.put(
        `${BASE_URL}/users/admin/online-status/${row.user_id}`,
        { is_online: next }
      );
      if (res.status === 200) {
        setTableData((prev) =>
          prev.map((r) =>
            r.user_id === row.user_id ? { ...r, is_online: next } : r
          )
        );
      }
    } catch (e) {
      alert("❌ Failed to toggle online status: " + (e?.response?.data?.message || e.message));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
  };

  const formatStatus = (status, user) => {
    if (!status) return "N/A";

    switch (status.toLowerCase()) {
      case "active":
        return "ACTIVE";
      case "unfilled":
        return "UNFILLED";
      case (user.status_code === 5 && user.approved.includes(1) && user.approved.includes(2) && user.approved.includes(3) && user.approved.includes(4)):
        return "TRAINING";
      default:
        return status;
    }
  };



  const rowBorderColors = ["#3B82F6", "#10B981", "#8B5CF6", "#F97316", "#EC4899", "#6366F1"];

  const getRowBorderStyle = (index) => {
    const color = rowBorderColors[index % rowBorderColors.length];
    return {
      borderTop: `2px solid ${color}`,
      borderBottom: `2px solid ${color}`
    };
  };

  const handleProfileClick = (user) => {
    setSelectedUser(user);
    const approvedArr = Array.isArray(user.approved)
      ? user.approved
      : (typeof user.approved === "string" ? (() => { try { return JSON.parse(user.approved); } catch(_) { return []; } })() : []);
    const statusUpper = (user.status || "").toUpperCase();

    if (user.status_code === 0) {
      setIsUnFilledPopupOpen(true);
    } else if (statusUpper === "UNFILLED") {
      if (user.status_code === 5 && approvedArr.length === 4 && (!user.rejected || user.rejected.length === 0)) {
        setIsProcessingPopupOpen(true);
      } else {
        setIsUnVerifiedPopupOpen(true);
      }
    } else {
      setIsActivatePopupOpen(true);
    }
  };


  let filteredData = tableData;

  if (searchText !== undefined && searchText.trim() !== "") {
    const search = searchText.trim().toLowerCase();
    filteredData = filteredData.filter((row) => (
      row.user_id?.toString().toLowerCase().includes(search) ||
      row.employer_name?.toLowerCase().includes(search) ||
      row.group?.toLowerCase().includes(search) ||
      row.email?.toLowerCase().includes(search) ||
      row.status?.toLowerCase().includes(search) ||
      row.phone_number?.includes(search)
    ));
  }

  const activeGroupFilter = filterOption || selectedOption;
  if (activeGroupFilter && activeGroupFilter !== "All") {
    filteredData = filteredData.filter((row) => row.group === activeGroupFilter);
  }

  const groupedData = useMemo(() => {
    const result = {};
    // Ensure all groups from database are listed automatically
    allGroupsList.forEach((grp) => {
      if (grp && typeof grp === "string") {
        result[grp] = [];
      }
    });
    // Populate users into their groups
    filteredData.forEach((item) => {
      const groupKey = item.group || "Unknown";
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
    });
    return result;
  }, [allGroupsList, filteredData]);

  const filters = userIdFiltersState || {};

  const parseAmount = (val) => {
    if (!val && val !== 0) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const n = Number(val.replace(/[^0-9.-]+/g, ""));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  };

  if (filters.statuses && Array.isArray(filters.statuses) && filters.statuses.length > 0) {
    filteredData = filteredData.filter(row => {
      return filters.statuses.some(status => {
        if (status === 'ACTIVE') {
          return row.status_code === 6 && row.status === 'ACTIVE' && row.login_image !== null;
        }
        if (status === 'UNACTIVE') {
          return row.status_code === 6 && row.status === 'ACTIVE' && row.login_image === null;
        }
        if (status === 5 || status === 'TRAINING') {
          return row.status_code === 5 && row.status === 'UNFILLED';
        }
        if (status === 1 || status === 2 || status === 3 || status === 4 || status === 'UNVERIFIED') {
          return [1, 2, 3, 4].includes(row.status_code) && row.status === 'UNFILLED';
        }
        if (status === 0 || status === 'UNFILLED') {
          return row.status_code === 0 && row.status === 'UNFILLED';
        }
        if (typeof status === 'number' || !isNaN(Number(status))) {
          return row.status_code === Number(status);
        }
        return row.status === status;
      });
    });
  }

  if (filters.bankEarned) {
    filteredData = filteredData.filter(row => parseAmount(row.bankedEarnings) > 0);
  }

  if (filters.paidEarnings) {
    filteredData = filteredData.filter(row => parseAmount(row.paidEarnings) > 0);
  }
  if (filters.pendingPayput) {
    filteredData = filteredData.filter(row => parseAmount(row.paymentDue) > 0);
  }
  if (filters.accountsCreated) {
    filteredData = filteredData.filter(row => (row.accountsCreated || 0) > 0);
  }
  if (filters.unRotedAccount) {
    filteredData = filteredData.filter(row => (row.unrootedAccounts || 0) > 0);
  }
  if (filters.tickets) {
    filteredData = filteredData.filter(row => (ticketCounts[row.user_id] || 0) > 0);
  }


  if (filters.accountsCreatedSort === "new") {
    filteredData = filteredData.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  } else if (filters.accountsCreatedSort === "old") {
    filteredData = filteredData.slice().sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
  }
  if (filters.joinedSort === "new") {
    filteredData = filteredData.slice().sort((a, b) => new Date(b.joinedDate) - new Date(a.joinedDate));
  } else if (filters.joinedSort === "old") {
    filteredData = filteredData.slice().sort((a, b) => new Date(a.joinedDate) - new Date(b.joinedDate));
  }

  if (filters.ticketSort === "high") {
    filteredData = filteredData.slice().sort((a, b) => (ticketCounts[b.user_id] || 0) - (ticketCounts[a.user_id] || 0));
  } else if (filters.ticketSort === "low") {
    filteredData = filteredData.slice().sort((a, b) => (ticketCounts[a.user_id] || 0) - (ticketCounts[b.user_id] || 0));
  }

  if (filters.bankedEarningsSort === "high") {
    filteredData = filteredData.slice().sort((a, b) => parseAmount(b.bankedEarnings) - parseAmount(a.bankedEarnings));
  } else if (filters.bankedEarningsSort === "low") {
    filteredData = filteredData.slice().sort((a, b) => parseAmount(a.bankedEarnings) - parseAmount(b.bankedEarnings));
  }
  if (filters.paidEarningsSort === "high") {
    filteredData = filteredData.slice().sort((a, b) => parseAmount(b.paidEarnings) - parseAmount(a.paidEarnings));
  } else if (filters.paidEarningsSort === "low") {
    filteredData = filteredData.slice().sort((a, b) => parseAmount(a.paidEarnings) - parseAmount(b.paidEarnings));
  }
  if (filters.paymentDueSort === "high") {
    filteredData = filteredData.slice().sort((a, b) => parseAmount(b.paymentDue) - parseAmount(a.paymentDue));
  } else if (filters.paymentDueSort === "low") {
    filteredData = filteredData.slice().sort((a, b) => parseAmount(a.paymentDue) - parseAmount(b.paymentDue));
  }

  const toggleGroup = (groupName) => {
    setOpenGroup((prev) => (prev === groupName ? null : groupName));
  };

  const groupBy = (array, key) => {
    return array.reduce((result, item) => {
      const groupKey = item[key] || "Unknown";
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
      return result;
    }, {});
  };

  const sumAmount = (rows, key) => rows.reduce((s, u) => s + parseAmount(u[key]), 0);
  const todayGroupTotals = Object.entries(groupedData).reduce(
    (acc, [groupName, users]) => {
      acc[groupName] = {
        paymentDue: sumAmount(users, "paymentDue"),
        bankedEarnings: sumAmount(users, "bankedEarnings"),
        paidEarnings: sumAmount(users, "paidEarnings"),
        companyCredits: sumAmount(users, "companyCredits"),
      };
      return acc;
    },
    {}
  );



  const homeTotals = useMemo(() => ({
    paymentDue: sumAmount(tableData, "paymentDue"),
    bankedEarnings: sumAmount(tableData, "bankedEarnings"),
    paidEarnings: sumAmount(tableData, "paidEarnings"),
    companyCredits: sumAmount(tableData, "companyCredits"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tableData]);

  const isToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };
  const todayTotals = useMemo(() => ({
    paymentDue: tableData.filter((u) => parseAmount(u.paymentDue) > 0 && isToday(u.updated_at)).length,
    bankedEarnings: tableData.filter((u) => parseAmount(u.bankedEarnings) > 0 && isToday(u.updated_at)).length,
    paidEarnings: tableData.filter((u) => parseAmount(u.paidEarnings) > 0 && isToday(u.updated_at)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tableData]);
  useEffect(() => {
    if (typeof onTodayTotals === 'function') {
      onTodayTotals(todayTotals);
    }
  }, [todayTotals, onTodayTotals]);
  return (
    <>
      <FilterBar
        selectedOption={selectedOption}
        setUserIdFilters={setUserIdFilters}
      />
      <div
        className="overflow-y-auto h-[calc(100vh-350px)] mt-2 pr-2 scrollbar-smooth scrollbar-thin postion-sticky"
        style={{ scrollBehavior: "smooth", scrollbarWidth: "thin" }}
      >
        {Object.entries(groupedData).map(([groupName, users]) => (
          <div key={groupName} className="mt-2 mb-6 border border-gray-300 rounded-lg shadow">
            <div
              className="sticky top-0 z-10 flex justify-between items-center bg-gray-200 px-4 py-2 cursor-pointer postion-sticky"
              onClick={() => toggleGroup(groupName)}
            >
              <div className="flex items-center">
                <h2 className="text-lg font-bold text-gray-800">
                  {groupName.toUpperCase()} Group
                </h2>
              </div>
              <span className="text-gray-600">
                <span className="mr-3 inline-flex items-center justify-center  w-7 h-7 rounded-full bg-gray-500 text-white font-bold text-base">
                  {users.length}
                </span>
                {openGroup === groupName ? "▲" : "▼"}
              </span>
            </div>

            {openGroup === groupName && (
              <>
                <div className="bg-white rounded-lg shadow mt-3">
                  <div className="overflow-auto max-h-[calc(100vh-430px)] scrollbar-thin rounded-t-lg">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-20 bg-gray-300 shadow-md">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          View Profile
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          User ID & Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Main-App Employee Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Phone Number & Joined Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-[#A80C0F] uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Live Payout Bills
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-[#FF0707] uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Payment Due
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-[#29A80C] uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Paid Earnings
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Banked Earnings
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-gray-950 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Super Bonus
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-red-500 uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          No. of Unrooted Accounts
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-[#29A80C] uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          No. of Accounts Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-extrabold text-[#0D0099] uppercase tracking-wider whitespace-nowrap border-b-2 border-gray-800">
                          Tickets
                        </th>

                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.length > 0 ? (
                        users.map((row, index) => (
                          <tr
                            key={row.user_id}
                            style={getRowBorderStyle(index)}
                            className="hover:bg-red-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <img
                                className="h-[54px] w-[54px] rounded-full object-cover border-2 border-gray-200"
                                src={row.profile_img }
                                alt={`${row.employer_name}'s profile`}
                                onClick={() => handleProfileClick(row)}
                              />
                              <div className="text-xs text-gray-500 mt-1">
                                {row.purpose}
                              </div>
                              <div className="flex items-center mt-1 gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleOnlineStatus(row); }}
                                  className={`px-2 py-0.5 text-[10px] rounded ${
                                    row.is_online ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
                                  }`}
                                  title="Toggle online/offline"
                                >
                                  {row.is_online ? 'Online' : 'Offline'}
                                </button>
                                {row.resubmitted ? (
                                  <span className="px-2 py-0.5 text-[10px] bg-yellow-200 text-yellow-900 rounded">
                                    Resubmitted
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {row.user_id}
                              </div>
                              <div
                                className={`text-sm ${row.status === "ACTIVE"
                                  ? "text-green-500"
                                  : row.status_code === 0
                                    ? "text-[#E19034]"
                                    : row.status_code === 1
                                      ? "text-red-600"
                                      : (row.status_code === 5 && row.status === "UNFILLED" && row.approved.length === 4)
                                        ? "text-[#B100AE]"
                                        : row.status === "REJECTED"
                                          ? "text-red-600"
                                          : "text-gray-500"
                                  }`}
                              >
                                {(row.status_code === 5 && row.status === "UNFILLED" && row.approved.length === 4
                                ) ? "TRAINING" : (row.login_image === null && row.status === "ACTIVE" ? "UNACTIVE" : row.status)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                              <div className="text-3xl flex items-center justify-between">
                                <button
                                  className="ml-4 text-gray-500 hover:text-blue-600 focus:outline-none"
                                  onClick={() => handleEditNameClick(row)}
                                  title="Edit Employee Name"
                                  disabled={editingNameUserId !== null && editingNameUserId !== row.user_id}
                                >
                                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                    <defs>
                                      <linearGradient id="uniformGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#D9D9D9" />
                                        <stop offset="100%" stopColor="#4203EF" />
                                      </linearGradient>
                                    </defs>
                                    <circle cx="5" cy="12" r="3" fill="url(#uniformGradient)" />
                                    <circle cx="12" cy="12" r="3" fill="url(#uniformGradient)" />
                                    <circle cx="19" cy="12" r="3" fill="url(#uniformGradient)" />
                                  </svg>
                                </button>
                              </div>

                              {editingNameUserId === row.user_id ? (
                                <div className="absolute z-50 bg-white border border-gray-300 rounded shadow-md p-2 mt-2 w-56">
                                  <label className="block text-xs text-gray-700 mb-1">Employee Name</label>
                                  <input
                                    type="text"
                                    value={editName}
                                    onChange={handleEditNameChange}
                                    className="w-full border rounded px-2 py-1 mb-2 text-sm"
                                    disabled={editLoading}
                                  />
                                  <div className="flex gap-2 justify-end">
                                    <button
                                      className="px-2 py-1 bg-green-500 text-white rounded flex items-center text-xs"
                                      onClick={() => handleEditNameSave(row.user_id)}
                                      disabled={editLoading}
                                    >
                                      <FaSave className="mr-1" /> Save
                                    </button>
                                    <button
                                      className="px-2 py-1 bg-gray-400 text-white rounded flex items-center text-xs"
                                      onClick={handleEditNameCancel}
                                      disabled={editLoading}
                                    >
                                      <FaTimes className="mr-1" /> Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-900 text-center">
                                  {row.employer_name || "---"}
                                </div>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 text-center">
                              {row.main_employee_code || "—"}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap  ">
                              <div className="text-3xl flex items-start justify-between mt-6">

                                <button
                                  className="ml-2 text-gray-500 hover:text-blue-600 focus:outline-none"
                                  onClick={() => handleEditPhoneClick(row)}
                                  title="Edit Phone Number"
                                  disabled={editingPhoneUserId !== null && editingPhoneUserId !== row.user_id}
                                >
                                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                    <defs>
                                      <linearGradient id="uniformGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#D9D9D9" />
                                        <stop offset="100%" stopColor="#4203EF" />
                                      </linearGradient>
                                    </defs>
                                    <circle cx="5" cy="12" r="3" fill="url(#uniformGradient)" />
                                    <circle cx="12" cy="12" r="3" fill="url(#uniformGradient)" />
                                    <circle cx="19" cy="12" r="3" fill="url(#uniformGradient)" />
                                  </svg>
                                </button>
                              </div>
                              {editingPhoneUserId === row.user_id ? (
                                <div className="absolute z-50 bg-white border border-gray-300 rounded shadow-md p-2 mt-2 w-56">
                                  <label className="block text-xs text-gray-700 mb-1">Phone Number</label>
                                  <input
                                    type="text"
                                    value={editPhone}
                                    onChange={handleEditPhoneChange}
                                    className="w-full border rounded px-2 py-1 mb-2 text-sm"
                                    disabled={editLoading}
                                    maxLength={10}
                                  />
                                  <div className="flex gap-2 justify-end">
                                    <button
                                      className="px-2 py-1 bg-green-500 text-white rounded flex items-center text-xs"
                                      onClick={() => handleEditPhoneSave(row.user_id)}
                                      disabled={editLoading}
                                    >
                                      <FaSave className="mr-1" /> Save
                                    </button>
                                    <button
                                      className="px-2 py-1 bg-gray-400 text-white rounded flex items-center text-xs"
                                      onClick={handleEditPhoneCancel}
                                      disabled={editLoading}
                                    >
                                      <FaTimes className="mr-1" /> Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-900">{row.phone_number}</div>
                              )}
                              <div className="text-sm text-[#3021D7]">{formatDate(row.joinedDate)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap mt-5">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="text-[#A80C0F] text-lg font-bold">
                                    ${row.livePayoutBills ?? 0}
                                  </div>
                                  <div className="text-sm text-[#5907FF] font-semibold">
                                    {row.totalCredits ?? 0} credits
                                  </div>
                                  <div className="text-sm text-[#3021D7]">{formatDateTime(row.updated_at)}</div>
                                </div>
                                <button
                                  className="ml-3 px-3 py-1.5 bg-[#A80C0F] hover:bg-red-800 text-white text-xs font-bold rounded shadow transition duration-150 ease-in-out cursor-pointer"
                                  onClick={() => handleEndLivePayout(row)}
                                  title="End live payout and move to Payment Due"
                                >
                                  End
                                </button>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap mt-5 relative">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[#FF0707] text-lg font-bold">
                                      ${row.paymentDue ?? 0}
                                    </span>
                                    <button
                                      className="text-gray-400 hover:text-blue-600 focus:outline-none cursor-pointer"
                                      onClick={() => handleEditPaymentDueClick(row)}
                                      title="Edit Payment Due"
                                      disabled={editingPaymentDueUserId !== null && editingPaymentDueUserId !== row.user_id}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                      </svg>
                                    </button>
                                  </div>
                                  {editingPaymentDueUserId === row.user_id ? (
                                    <div className="absolute z-50 bg-white border border-gray-300 rounded shadow-md p-2 mt-2 w-56">
                                      <label className="block text-xs text-gray-700 mb-1">Payment Due ($)</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={editPaymentDue}
                                        onChange={handleEditPaymentDueChange}
                                        className="w-full border rounded px-2 py-1 mb-2 text-sm"
                                        disabled={editLoading}
                                      />
                                      <div className="flex gap-2 justify-end">
                                        <button
                                          className="px-2 py-1 bg-green-500 text-white rounded flex items-center text-xs cursor-pointer"
                                          onClick={() => handleEditPaymentDueSave(row.user_id)}
                                          disabled={editLoading}
                                        >
                                          <FaSave className="mr-1" /> Save
                                        </button>
                                        <button
                                          className="px-2 py-1 bg-gray-400 text-white rounded flex items-center text-xs cursor-pointer"
                                          onClick={handleEditPaymentDueCancel}
                                          disabled={editLoading}
                                        >
                                          <FaTimes className="mr-1" /> Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="text-sm text-[#3021D7]">{formatDateTime(row.updated_at)}</div>
                                  <div className="text-sm text-blue-600">(EDITED BY)</div>
                                  <div className="text-sm text-blue-400">{row.paymentDueIsEditedBy || "-"}</div>
                                </div>
                                <button
                                  className="ml-3 px-3 py-1.5 bg-[#29A80C] hover:bg-green-700 text-white text-xs font-bold rounded shadow transition duration-150 ease-in-out cursor-pointer"
                                  onClick={() => openSettle(row)}
                                  title="End payment due and move to Paid Earnings"
                                >
                                  End
                                </button>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-[#29A80C]">
                              ${row.paidEarnings}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-3xl flex items-center mt-10">
                              </div>
                              <div className="text-lg text-[#A60CA8] font-bold">
                                ${row.bankedEarnings}
                              </div>
                              <div className="text-sm text-blue-600">(Updated at)</div>
                              <div className="text-sm text-[#3021D7]">{formatDateTime(row.updated_at)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-[#5907FF]">
                              ${row.superBonus ?? 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="text-[#FF0E12] text-lg font-bold">
                                {row.unrootedAccounts}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="text-[#E19034] text-lg font-bold">
                                {row.accountsCreated}
                              </div>
                            </td>

                            <td
                              className="px-6 py-4 whitespace-nowrap text-center"
                              onClick={() => {
                                setSelectedUser(row);
                                setIsModalOpen(true);
                              }}
                            >
                              <div className="text-lg font-extrabold text-[#0D0099]">
                                {ticketCounts[row.user_id] ?? 0}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="13" className="text-center py-4">No matching records found as of Now.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                  <div className='mx-2 w-full h-[132px] bg-blue-200 flex flex-col items-center justify-center
                           border-2 border-red-500 mt-4 shadow-lg rounded-lg'>
                    <div className='flex items-center justify-between w-full px-4 text-[#29A80C] font-bold'>
                      
                      <p>NO OF LAST PAID EARNINGS ACCOUNTS - {groupName}</p>
                      <span>${(todayGroupTotals[groupName]?.paidEarnings || 0).toFixed(2)}</span>
                    </div>
                    <div className='flex items-center justify-between w-full px-4 text-[#FF0707] font-bold'>
                      <p>NO OF PENDING PAYMENT DUES ACCOUNTS - {groupName}</p>
                      <span>${(todayGroupTotals[groupName]?.paymentDue || 0).toFixed(2)}</span>
                    </div>
                    <div className='flex items-center justify-between w-full px-4 text-[#A60CA8] font-bold'>
                      <p>NO OF BANKED EARNINGS ACCOUNTS- {groupName}</p>
                      <span>${(todayGroupTotals[groupName]?.bankedEarnings || 0).toFixed(2)}</span>
                    </div>
                    <div className='flex items-center justify-between w-full px-4 text-[#E19034] font-bold'>
                      <p>COMPANY CREDITS (UN-FETCHED) - {groupName}</p>
                      <span>{(todayGroupTotals[groupName]?.companyCredits || 0)} credits</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        <div className='mx-2 w-full h-[132px] bg-blue-200 flex flex-col items-center justify-center
                           border-2 border-red-500 mt-4 shadow-lg rounded-lg'>
          <div className='flex items-center justify-between w-full px-4 text-[#29A80C] font-bold'>
            <p>NO OF LAST PAID EARNINGS ACCOUNTS</p>
            <span>${(homeTotals.paidEarnings || 0).toFixed(2)}</span>
          </div>

          <div className='flex items-center justify-between w-full px-4 text-[#FF0707] font-bold'>
            <p>NO OF LAST PAYMENT DUES ACCOUNTS</p>
            <span>${(homeTotals.paymentDue || 0).toFixed(2)}</span>
          </div>
          <div className='flex items-center justify-between w-full px-4 text-[#A60CA8] font-bold'>
            <p>NO OF BANKED EARNINGS ACCOUNTS</p>
            <span>${(homeTotals.bankedEarnings || 0).toFixed(2)}</span>
          </div>
          <div className='flex items-center justify-between w-full px-4 text-[#E19034] font-bold'>
            <p>COMPANY CREDITS (UN-FETCHED, THIS MONTH)</p>
            <span>{(homeTotals.companyCredits || 0)} credits</span>
          </div>
          <div className='flex items-center justify-between w-full px-4 text-[#B45309] font-bold'>
            <p>COMPANY CREDITS (LOCKED, ALL-TIME)</p>
            <span>{(companyLocked.total || 0)} credits</span>
          </div>
        </div>
      </div>
      {isModalOpen && selectedUser && (
        <RaiseTickets
          user_id={selectedUser.user_id}
          onClose={() => setIsModalOpen(false)}
          reloadTableData={reloadTableData}
        />
      )}
      {isActivatePopupOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ActivatePopup
            user={selectedUser}
            onClose={() => {
              setIsActivatePopupOpen(false);
              reloadTableData();
            }}
          />
        </div>
      )}
      {isUnFilledPopupOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <UnFilledPopup
            user={selectedUser}
            onClose={() => {
              setIsUnFilledPopupOpen(false);
              reloadTableData();
            }}
          />
        </div>
      )}
      {isUnVerifiedPopupOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <UnVerifiedPopup
            user={selectedUser}
            onClose={() => {
              setIsUnVerifiedPopupOpen(false);
              reloadTableData();
            }}
          />
        </div>
      )}
      {isProcessingPopupOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ProcessingPopup
            user={selectedUser}
            onClose={() => {
              setIsProcessingPopupOpen(false);
              reloadTableData();
            }}
          />
        </div>
      )}
      {livePayoutDetailRow && (
        <LivePayoutDetailPopup
          row={livePayoutDetailRow}
          onClose={() => setLivePayoutDetailRow(null)}
        />
      )}
      {settleRow && (
        <PaymentSettlePopup
          row={settleRow}
          onClose={() => setSettleRow(null)}
          onSettled={() => {
            setSettleRow(null);
            reloadTableData();
          }}
        />
      )}
    </>
  );
};

export default MainTable;